import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { UI, DISPLAY } from '../styles/tl5';
import { ROUTES } from '../constants/routes';
import { useListFaqsQuery, useGetContactInfoQuery } from '../store/siteContentApi';

// Help — "Help & contact", built to the v5 reference. It is the reference's
// merge of our two old pages: /support (contact + ticket form) and /faq.
//
// Four differences from the reference, each forced by what we actually have:
//
//   · The reference filters the FAQs with six topic chips (Booking, Home
//     collection, Reports, …). GET /faqs returns {question, answer,
//     sort_order} and no category at all, so there is nothing to filter on
//     and nothing to print in the per-question eyebrow. Both are left out
//     rather than faked; the search box, which is the reference's own
//     primary filter, works on the real question and answer text.
//     API-NOTES 2.25.
//   · A fifth contact card, "Raise a ticket". The reference's Help has no
//     ticket flow, but ours works, is tracked at /my-tickets, and is the
//     only route with a paper trail — dropping it would quietly remove
//     the one support channel a customer can follow up on.
//   · The cancellation table carries the figures from our published Terms
//     of service, not the reference's prototype ones. The reference
//     invents a ₹150 visit charge and a 2-4 day refund window; neither
//     appears in our terms, which say free until two hours before, 50%
//     inside two hours, and seven business days. API-NOTES 2.24.
//   · The "same-day report guarantee" box drops the reference's 12-noon
//     cutoff, which we do not publish anywhere, and states the turnaround
//     About already commits to.

const FALLBACK = {
  phone: '18005995656',
  whatsapp: '+919014638633',
  email: 'customercare@mytrustlab.com',
  support_hours: '9:30 AM – 6:00 PM daily',
};

// From "Refunds & cancellations" in GET /legal/terms. Kept in step with the
// same table on the booking page — see the note on Booking's CANCELLATION.
const CANCELLATION = [
  ['More than 2 hours before your slot', 'Nothing. Full refund.', true],
  ['Within 2 hours of your slot', '50% refund', false],
  ['After the sample is collected', 'No refund', false],
];

const prettyPhone = (raw) => {
  const d = String(raw || '').replace(/\D/g, '');
  if (d.length === 12 && d.startsWith('91')) return `+91 ${d.slice(2, 7)} ${d.slice(7)}`;
  if (d.length === 10) return `${d.slice(0, 5)} ${d.slice(5)}`;
  if (d.length === 11) return `${d.slice(0, 4)} ${d.slice(4, 7)} ${d.slice(7)}`;
  return raw;
};

// ── shared fragments ──────────────────────────────────────────────────
const card = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  minWidth: 0,
  p: 'clamp(20px, 3.4vw, 32px)',
  borderRadius: '28px',
  bgcolor: '#FFFFFF',
  textDecoration: 'none',
  transition: 'transform .2s var(--tl-ease)',
  '&:hover': { transform: 'translateY(-2px)' },
};

const cardTitle = {
  fontFamily: DISPLAY,
  fontWeight: 700,
  fontSize: 22,
  lineHeight: 1.15,
  letterSpacing: '-.01em',
};

const cardSub = { fontFamily: UI, fontSize: 14, lineHeight: 1.5, color: 'var(--tl-muted-2)' };

const Help = () => {
  const { data: faqs = [], isLoading } = useListFaqsQuery();
  const { data: contact } = useGetContactInfoQuery();

  const [q, setQ] = useState('');
  const [open, setOpen] = useState(0);

  const phone = contact?.phone || FALLBACK.phone;
  const whatsapp = String(contact?.whatsapp || FALLBACK.whatsapp).replace(/\D/g, '');
  const email = contact?.email || FALLBACK.email;
  const hours = contact?.support_hours || FALLBACK.support_hours;

  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const sorted = [...faqs].sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));
    if (!needle) return sorted;
    return sorted.filter((f) => `${f.question} ${f.answer}`.toLowerCase().includes(needle));
  }, [faqs, q]);

  const onSearch = (e) => { setQ(e.target.value); setOpen(-1); };

  const CONTACT_CARDS = [
    {
      key: 'whatsapp',
      href: `https://wa.me/${whatsapp}`,
      external: true,
      badge: 'Fastest',
      title: 'WhatsApp us',
      sub: `${prettyPhone(whatsapp)} · ${hours}`,
      dark: true,
    },
    { key: 'call', href: `tel:${String(phone).replace(/\D/g, '')}`, title: 'Call us', sub: `${prettyPhone(phone)} (toll-free)` },
    { key: 'email', href: `mailto:${email}`, title: 'Email', sub: email },
    { key: 'ticket', to: ROUTES.SUPPORT, title: 'Raise a ticket', sub: 'Track your question to an answer →' },
    { key: 'branch', to: ROUTES.CENTRES, title: 'Visit a branch', sub: 'Find a Care Centre near you →' },
  ];

  return (
    <Box>
      {/* ── 1. hero ──────────────────────────────────────────────────── */}
      <Box component="section" sx={{ pt: 2, px: 2 }}>
        <Box
          sx={{
            width: '100%',
            maxWidth: 1320,
            mx: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
            p: 'clamp(28px, 5vw, 56px)',
            borderRadius: '36px',
            bgcolor: 'var(--tl-teal)',
            color: '#FFFFFF',
          }}
        >
          <Typography
            component="h1"
            sx={{
              m: 0,
              fontFamily: DISPLAY,
              fontWeight: 700,
              fontSize: 'clamp(34px, 5.4vw, 72px)',
              lineHeight: 0.95,
              letterSpacing: '-.035em',
              textWrap: 'balance',
              color: '#FFFFFF',
            }}
          >
            How can we help?
          </Typography>
          <Typography sx={{ m: 0, fontFamily: UI, fontSize: 18, lineHeight: 1.55, color: '#E6FAF5', maxWidth: 620 }}>
            Search common questions, or talk to a real person on WhatsApp.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 0.75,
              maxWidth: 620,
              p: 0.75,
              borderRadius: '18px',
              bgcolor: '#FFFFFF',
            }}
          >
            <Box
              component="input"
              value={q}
              onChange={onSearch}
              type="search"
              aria-label="Search help questions"
              placeholder="e.g. reschedule, refund, fasting"
              sx={{
                flex: 1,
                minWidth: 0,
                border: 0,
                outline: 0,
                font: 'inherit',
                fontFamily: UI,
                fontSize: 16,
                p: '12px 14px',
                bgcolor: 'transparent',
                color: 'var(--tl-g9)',
              }}
            />
          </Box>
        </Box>
      </Box>

      <Box
        component="main"
        sx={{ width: '100%', maxWidth: 1320, mx: 'auto', px: 2, pt: 1.5, pb: 9, display: 'flex', flexDirection: 'column', gap: 1.5 }}
      >
        {/* ── 2. how to reach us ─────────────────────────────────────── */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
            gap: 1.5,
          }}
        >
          {CONTACT_CARDS.map((c) => {
            const inner = (
              <>
                {c.badge && (
                  <Box
                    component="span"
                    sx={{
                      alignSelf: 'start',
                      px: '10px',
                      py: '6px',
                      mb: 0.5,
                      borderRadius: '999px',
                      bgcolor: 'var(--tl-yellow)',
                      color: 'var(--tl-g9)',
                      fontFamily: UI,
                      fontSize: 12,
                      fontWeight: 800,
                    }}
                  >
                    {c.badge}
                  </Box>
                )}
                <Box component="span" sx={{ ...cardTitle, color: c.dark ? '#FFFFFF' : 'var(--tl-g9)' }}>
                  {c.title}
                </Box>
                <Box component="span" sx={{ ...cardSub, color: c.dark ? '#C7DDD7' : 'var(--tl-muted-2)' }}>
                  {c.sub}
                </Box>
              </>
            );
            const sx = { ...card, ...(c.dark ? { bgcolor: 'var(--tl-g9)', color: '#FFFFFF' } : null) };
            return c.to
              ? <Box key={c.key} component={Link} to={c.to} sx={sx}>{inner}</Box>
              : (
                <Box
                  key={c.key}
                  component="a"
                  href={c.href}
                  {...(c.external ? { target: '_blank', rel: 'noopener noreferrer' } : null)}
                  sx={sx}
                >
                  {inner}
                </Box>
              );
          })}
        </Box>

        {/* ── 3. questions ───────────────────────────────────────────── */}
        <Box component="section" sx={{ ...card, gap: 0, mt: 2, '&:hover': null }}>
          {isLoading && [0, 1, 2, 3, 4].map((i) => (
            <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, py: 2.5, borderBottom: '1px solid #EEF3F1' }}>
              <Box sx={{ height: 18, width: '56%', borderRadius: '6px', bgcolor: 'var(--tl-mint-2)' }} />
              <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: 'var(--tl-mint)' }} />
            </Box>
          ))}

          {!isLoading && list.length === 0 && (
            <Box component="span" sx={{ py: 2, fontFamily: UI, fontSize: 16, color: 'var(--tl-muted-2)' }}>
              {faqs.length
                ? 'No matching questions. Try another word, or message us on WhatsApp.'
                : 'Questions are being added. Message us on WhatsApp and we will answer straight away.'}
            </Box>
          )}

          {!isLoading && list.map((f, i) => {
            const isOpen = open === i;
            return (
              <Box key={f._id || f.question} sx={{ borderBottom: '1px solid #EEF3F1' }}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2,
                    background: 'none',
                    border: 0,
                    p: '20px 0',
                    font: 'inherit',
                    fontFamily: UI,
                    fontSize: 17,
                    fontWeight: 800,
                    color: 'var(--tl-g9)',
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  <Box component="span" sx={{ minWidth: 0 }}>{f.question}</Box>
                  <Box
                    component="span"
                    aria-hidden="true"
                    sx={{
                      width: 32,
                      height: 32,
                      flexShrink: 0,
                      borderRadius: '50%',
                      bgcolor: 'var(--tl-mint)',
                      display: 'grid',
                      placeItems: 'center',
                      fontSize: 18,
                    }}
                  >
                    {isOpen ? '−' : '+'}
                  </Box>
                </Box>
                {isOpen && (
                  <Typography sx={{ m: '0 0 20px', fontFamily: UI, fontSize: 16, lineHeight: 1.65, color: 'var(--tl-muted)', maxWidth: 760 }}>
                    {f.answer}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>

        {/* ── 4. cancellation and refunds ────────────────────────────── */}
        <Box component="section" sx={{ ...card, gap: 2, mt: 2, '&:hover': null }}>
          <Typography
            component="h2"
            sx={{ m: 0, fontFamily: DISPLAY, fontWeight: 700, fontSize: 'clamp(26px, 3vw, 36px)', letterSpacing: '-.02em', color: 'var(--tl-g9)' }}
          >
            Cancellation and refunds
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              border: '1.5px solid #E1ECE8',
              borderRadius: '18px',
              overflow: 'hidden',
              fontFamily: UI,
              fontSize: 15,
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)',
                gap: 1.5,
                p: '12px 18px',
                bgcolor: 'var(--tl-mint)',
                fontWeight: 800,
                color: 'var(--tl-g9)',
              }}
            >
              <Box component="span">When you cancel</Box>
              <Box component="span">What you pay</Box>
            </Box>
            {CANCELLATION.map(([when, what, good]) => (
              <Box
                key={when}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)',
                  gap: 1.5,
                  p: '13px 18px',
                  borderTop: '1px solid #E1ECE8',
                  color: 'var(--tl-g9)',
                }}
              >
                <Box component="span">{when}</Box>
                <Box component="span" sx={{ fontWeight: 800, color: good ? 'var(--tl-teal-d)' : 'inherit' }}>{what}</Box>
              </Box>
            ))}
          </Box>

          <Box component="span" sx={{ fontFamily: UI, fontSize: 13, color: 'var(--tl-muted-2)' }}>
            Refunds reach your original payment method within 7 business days. The full wording is in our{' '}
            <Box component={Link} to={`${ROUTES.LEGAL}#refunds`} sx={{ color: 'var(--tl-teal-d)', fontWeight: 800 }}>
              terms
            </Box>.
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, p: '16px 18px', borderRadius: '18px', bgcolor: 'var(--tl-mint)' }}>
            <Box component="span" sx={{ fontFamily: UI, fontWeight: 800, color: 'var(--tl-g9)' }}>
              How soon your report arrives
            </Box>
            <Box component="span" sx={{ fontFamily: UI, fontSize: 14, lineHeight: 1.55, color: 'var(--tl-muted)' }}>
              Most routine reports are ready in about six hours. Specialty, culture, histopathology
              and genomics tests have their own timelines, shown on each test page.
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Help;
